import { KeyboardArrowDownRounded } from '@mui/icons-material';
import {
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useStateRef } from 'app/services';
import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import InputMask from 'react-input-mask';

const TrailerForm = forwardRef((props, ref) => {
  const theme = useTheme();
  const [numberOfTank, setNumberOfTank] = useState(defaultValues ? defaultValues.tank.length : 0);
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(!!defaultValues);
  const { defaultValues, onSubmit } = props;
  const { register, reset, control, formState, handleSubmit } = useForm({
    defaultValues: {
      number: defaultValues ? defaultValues.number : '',
      vin: defaultValues ? (defaultValues.vin ? defaultValues.vin : '') : '',
      fuel_level_sensor: defaultValues ? defaultValues.fuel_level_sensor : '',
      fuel_availability_sensor: defaultValues ? defaultValues.fuel_availability_sensor : false,
      drain_sensor: defaultValues ? defaultValues.drain_sensor : false,
      ground_sensor: defaultValues ? defaultValues.ground_sensor : false,
      dun_cover: defaultValues ? defaultValues.dun_cover : false,
      tank: [],
      drain_side: defaultValues ? defaultValues.drain_side : 'none',
    },
  });

  const { fields, append, remove } = useFieldArray({ name: 'tank', control });

  const handleTankChange = (event) => {
    setNumberOfTank(event.target.value);
  };

  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) => {
        onSubmit({
          number: data.number,
          vin: data.vin ? data.vin : undefined,
          fuel_level_sensor: Number(data.fuel_level_sensor),
          tank: data.tank,
          fuel_availability_sensor: data.fuel_availability_sensor ? 1 : 0,
          drain_sensor: data.drain_sensor ? 1 : 0,
          ground_sensor: data.ground_sensor ? 1 : 0,
          dun_cover: data.dun_cover ? 1 : 0,
          drain_side: data.drain_side ?? 'none',
        });
      })();
    },
  }));

  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined), theme.transitions.duration.shorter);
    }
  }, [firstUpdateRef.current]);

  useEffect(() => {
    if (defaultValues) {
      reset({
        number: defaultValues.number,
        vin: defaultValues.vin ? defaultValues.vin : '',
        fuel_level_sensor: defaultValues.fuel_level_sensor,
        tank: defaultValues.tank.map(([value, type]) => ({
          value,
          type,
        })),
        tank_count: defaultValues.tank.length,
        fuel_availability_sensor: defaultValues.fuel_availability_sensor,
        drain_sensor: defaultValues.drain_sensor,
        ground_sensor: defaultValues.ground_sensor,
        dun_cover: defaultValues.dun_cover,
        drain_side: defaultValues.drain_side ?? 'none',
      });

      setNumberOfTank(defaultValues.tank.length);
    }
  }, [defaultValues]);

  useEffect(() => {
    if (!firstUpdateRef.current) {
      const countFields = fields.length;

      if (numberOfTank > countFields) {
        for (let i = countFields; i < numberOfTank; i++) {
          const [value, type] = defaultValues?.tank[i] ? defaultValues.tank[i] : ['', ''];
          append({ value: value, type: type });
        }
      } else {
        for (let i = countFields; i > numberOfTank; i--) {
          remove(i - 1);
        }
      }
    }
  }, [numberOfTank]);
  return (
    <form>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.number} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Регистрационный номер</InputLabel>
            <Controller
              name='number'
              control={control}
              render={({ field }) => (
                <OutlinedInput
                  label='Регистрационный номер'
                  variant='outlined'
                  notched={firstUpdate}
                  fullWidth
                  placeholder='АА000077, А000АА77, А000АА777, A0000A7, AA00000, 0000AA0, 00AA000'
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    required: true,
                    pattern:
                      /^([АВЕКМНОРСТУХ]{2}\d{6}|[АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2}\d{2}|[АВЕКМНОРСТУХ]\d{3}[АВЕКМНОРСТУХ]{2}\d{3}|[A-Z]\d{4}[A-Z]\d|[A-Z]{2}\d{5}|\d{4}[A-Z]{2}\d|\d{2}[A-Z]{2}\d{3})$/,
                  })}></OutlinedInput>
              )}></Controller>
            <FormHelperText>
              Используйте регистрационный номер в указанном формате: кириллица для [АА000077, А000АА77, А000АА777], латиница для [A0000A7,
              AA00000, 0000AA0, 00AA000]
            </FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.vin} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>VIN номер</InputLabel>
            <Controller
              name='vin'
              control={control}
              render={({ field }) => (
                <InputMask
                  mask='AAAAAAAAAAAAAAAAA'
                  maskChar=''
                  formatChars={{
                    A: '[A-HJ-NPR-Z0-9]',
                  }}
                  value={field.value}
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[A-HJ-NPR-Z0-9]{17}$/iu,
                    minLength: 17,
                  })}>
                  {(inputProps) => (
                    <OutlinedInput
                      label='VIN номер'
                      variant='outlined'
                      notched={firstUpdate}
                      fullWidth
                      placeholder='1HGBH41JXMN109186'
                      {...inputProps}></OutlinedInput>
                  )}
                </InputMask>
              )}></Controller>
            <FormHelperText>Используйте VIN номер согласно стандартам ISO 3779-1983 и ISO 3780</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.fuel_level_sensor} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Номер датчика уровня топлива</InputLabel>
            <Controller
              name='fuel_level_sensor'
              control={control}
              render={({ field }) => (
                <OutlinedInput
                  label='Номер датчика уровня топлива'
                  variant='outlined'
                  notched={firstUpdate}
                  fullWidth
                  placeholder='135790'
                  onChange={field.onChange}
                  {...register(field.name, {
                    pattern: /^[0-9]{3,9}$/iu,
                    required: true,
                  })}
                  {...field}
                />
              )}></Controller>
            <FormHelperText>Укажите номер датчика уровня топлива, используйте от 3 до 9 символов</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      <Typography variant='caption'>Выберите установленное оборудование на ППЦ:</Typography>
      <Grid
        container
        sx={(theme) => ({
          marginTop: theme.spacing(3),
          '.MuiCheckbox-root': {
            padding: '0 9px',
            '&:not(.Mui-checked) svg': {
              color: '#d5d5d5',
            },
          },
        })}>
        <Grid item xs={6}>
          <Grid container spacing={2}>
            <FormControlLabel
              sx={() => ({
                margin: 0,
              })}
              label='Датчики наличия топлива'
              control={
                <Controller
                  name='fuel_availability_sensor'
                  control={control}
                  render={({ field }) => (
                    <Checkbox size='small' disableRipple checked={!!field.value} onChange={field.onChange} {...register(field.name)} />
                  )}></Controller>
              }
            />
          </Grid>
          <Grid
            container
            spacing={2}
            sx={(theme) => ({
              marginTop: theme.spacing(1),
            })}>
            <FormControlLabel
              sx={() => ({
                margin: 0,
              })}
              label='УЗА-М'
              control={
                <Controller
                  name='ground_sensor'
                  control={control}
                  render={({ field }) => (
                    <Checkbox size='small' disableRipple checked={!!field.value} {...register(field.name)} />
                  )}></Controller>
              }
            />
          </Grid>
        </Grid>
        <Grid item xs={6}>
          <Grid container spacing={2}>
            <FormControlLabel
              sx={() => ({
                margin: 0,
              })}
              label='Датчики давления воздуха'
              control={
                <Controller
                  name='drain_sensor'
                  control={control}
                  render={({ field }) => (
                    <Checkbox size='small' disableRipple checked={!!field.value} {...register(field.name)} />
                  )}></Controller>
              }
            />
          </Grid>
          <Grid
            container
            spacing={2}
            sx={(theme) => ({
              marginTop: theme.spacing(1),
            })}>
            <FormControlLabel
              sx={() => ({
                margin: 0,
              })}
              label='Второй ящик (новый)'
              control={
                <Controller
                  name='dun_cover'
                  control={control}
                  render={({ field }) => (
                    <Checkbox size='small' disableRipple checked={!!field.value} {...register(field.name)} />
                  )}></Controller>
              }
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.drain_side} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Сторона слива</InputLabel>
            <Controller
              name='drain_side'
              control={control}
              render={({ field }) => (
                <Select
                  label='Сторона слива'
                  defaultValue='none'
                  IconComponent={KeyboardArrowDownRounded}
                  notched={firstUpdate}
                  MenuProps={{
                    sx: {
                      '.MuiPaper-root:before': {
                        display: 'none',
                      },
                    },
                  }}
                  {...register(field.name, {
                    required: fields.length !== 0,
                  })}
                  value={field.value}
                  onChange={(e) => field.onChange(e.target.value)}>
                  {config.drainSideOptions.map((option, key) => (
                    <MenuItem key={key} disableRipple value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              )}></Controller>
            <FormHelperText>Выберите сторону слива ППЦ</FormHelperText>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <FormControl error={!!formState.errors.tank_count} margin='normal' variant='outlined' fullWidth>
            <InputLabel disableAnimation={firstUpdate}>Количество секций</InputLabel>
            <Controller
              name='tank_count'
              control={control}
              render={({ field }) => (
                <Select
                  label='Количество секций'
                  defaultValue='0'
                  IconComponent={KeyboardArrowDownRounded}
                  notched={firstUpdate}
                  MenuProps={{
                    sx: {
                      '.MuiPaper-root:before': {
                        display: 'none',
                      },
                    },
                  }}
                  {...register(field.name, {
                    pattern: /^[0-9]$/iu,
                    required: true,
                  })}
                  value={numberOfTank}
                  onChange={(event) => {
                    handleTankChange(event);
                    field.onChange(event);
                  }}>
                  <MenuItem value='0'>Нет</MenuItem>
                  {config.numberOfTank.map((value, key) => (
                    <MenuItem key={key} disableRipple value={value}>
                      {value}
                    </MenuItem>
                  ))}
                </Select>
              )}></Controller>
            <FormHelperText>Выберите количество секций цистерны</FormHelperText>
          </FormControl>
        </Grid>
      </Grid>
      {fields.length !== 0 &&
        fields.map((field, i) => (
          <React.Fragment key={field.id}>
            <Divider
              textAlign='center'
              sx={(theme) => ({
                marginTop: theme.spacing(1.25),
              })}>
              <Typography variant='body2'>Секция {i + 1}</Typography>
            </Divider>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl error={!!formState.errors.tank?.[i]?.value} margin='normal' variant='outlined' fullWidth>
                  <InputLabel disableAnimation={firstUpdate}>Объем секции</InputLabel>
                  <Controller
                    name={`tank.${i}.value`}
                    control={control}
                    render={({ field }) => (
                      <InputMask
                        mask='99999'
                        maskChar=''
                        {...register(field.name, {
                          pattern: /^[0-9]{1,5}$/iu,
                          required: true,
                        })}
                        {...field}
                        ref={() => {
                          field.ref({
                            focus: () => ({}),
                          });
                        }}>
                        {(inputProps) => (
                          <OutlinedInput
                            label='Объем секции'
                            variant='outlined'
                            notched={firstUpdate}
                            onChange={field.onChange}
                            {...inputProps}></OutlinedInput>
                        )}
                      </InputMask>
                    )}></Controller>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <FormControl
                  error={!!formState.errors.tank?.[i]?.type}
                  margin='normal'
                  variant='outlined'
                  fullWidth
                  sx={{
                    marginTop: '16px!important',
                  }}>
                  <InputLabel disableAnimation={firstUpdate}>Тип топлива</InputLabel>
                  <Controller
                    name={`tank.${i}.type`}
                    control={control}
                    render={({ field }) => (
                      <Select
                        label='Тип топлива'
                        IconComponent={KeyboardArrowDownRounded}
                        MenuProps={{
                          sx: {
                            '.MuiPaper-root:before': {
                              display: 'none',
                            },
                          },
                        }}
                        {...register(field.name, {
                          required: true,
                        })}
                        notched={firstUpdate}
                        {...field}
                        ref={() => {
                          field.ref({
                            focus: () => ({}),
                          });
                        }}>
                        {['АИ', 'ДТ', 'Битум'].map((value) => (
                          <MenuItem key={value} disableRipple value={value}>
                            {value}
                          </MenuItem>
                        ))}
                      </Select>
                    )}></Controller>
                </FormControl>
              </Grid>
            </Grid>
          </React.Fragment>
        ))}
    </form>
  );
});

export default TrailerForm;
